/**
 * useDigitalCoworkerChat - SSE streaming hook for Digital Coworker chat.
 *
 * Maintains a local session message history and streams responses from
 * POST /v1/digital-coworkers/{id}/chat. No conversation persistence –
 * each session is ephemeral and local to the Sheet component.
 */

import { useCallback, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { UIMessage, SourcePart } from '@/features/chatV2/types'
import { apiService } from '@/service/api'

interface DcChatChunk {
  role?: string
  content?: string
  timestamp?: string
  agent_used?: string
  sources?: Array<{ name: string; type: string; score: number }>
}

function buildUserMessage(text: string): UIMessage {
  return {
    id: uuidv4(),
    role: 'user',
    parts: [{ type: 'text', text }],
    createdAt: new Date(),
    status: 'ready',
  }
}

function buildAssistantPlaceholder(): UIMessage {
  return {
    id: uuidv4(),
    role: 'assistant',
    parts: [{ type: 'text', text: '' }],
    createdAt: new Date(),
    status: 'streaming',
  }
}

function applyChunkToMessage(msg: UIMessage, chunk: DcChatChunk): UIMessage {
  const existingText =
    (msg.parts.find((p) => p.type === 'text') as { type: 'text'; text: string } | undefined)
      ?.text ?? ''

  // Accumulate content (backend may stream partial chunks or a single full chunk)
  const newText = chunk.content != null ? existingText + chunk.content : existingText

  const sourceParts: SourcePart[] = (chunk.sources ?? []).map((s) => ({
    type: 'source',
    sourceId: `dc-source-${s.name}-${s.type}`,
    title: s.name,
    content: s.type,
    confidence: s.score,
  }))

  const nonSourceParts = msg.parts.filter((p) => p.type !== 'source')

  return {
    ...msg,
    parts: [
      { type: 'text', text: newText },
      ...nonSourceParts.filter((p) => p.type !== 'text'),
      ...sourceParts,
    ],
    status: 'streaming',
  }
}

function finalizeMessage(msg: UIMessage): UIMessage {
  return { ...msg, status: 'ready', createdAt: new Date() }
}

export interface UseDigitalCoworkerChatReturn {
  messages: UIMessage[]
  isLoading: boolean
  input: string
  setInput: (value: string) => void
  sendMessage: (text?: string) => Promise<void>
  clearMessages: () => void
}

export function useDigitalCoworkerChat(
  coworkerId: string
): UseDigitalCoworkerChatReturn {
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [input, setInput] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  const sendMessage = useCallback(
    async (text?: string) => {
      const content = (text ?? input).trim()
      if (!content || isLoading) return

      // Abort any in-flight request
      abortRef.current?.abort()
      abortRef.current = new AbortController()
      const signal = abortRef.current.signal

      const userMsg = buildUserMessage(content)
      const assistantMsg = buildAssistantPlaceholder()
      const assistantId = assistantMsg.id

      setInput('')
      setMessages((prev) => [...prev, userMsg, assistantMsg])
      setIsLoading(true)

      try {
        const response = await apiService.chatWithDigitalCoworker(coworkerId, content, signal)

        if (!response.body) {
          throw new Error('No response body from digital coworker chat')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          // Keep the last (potentially incomplete) line in the buffer
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed) continue

            // Handle "event: done" – finalize the assistant message
            if (trimmed === 'event: done') {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? finalizeMessage(m) : m))
              )
              continue
            }

            if (!trimmed.startsWith('data:')) continue

            const raw = trimmed.slice('data:'.length).trim()
            if (!raw || raw === '{}') continue

            try {
              const chunk: DcChatChunk = JSON.parse(raw)
              // Skip system messages — they are internal and should never be rendered
              if (chunk.role === 'system') continue
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? applyChunkToMessage(m, chunk) : m
                )
              )
            } catch {
              // Non-JSON data line – skip
            }
          }
        }

        // Ensure the message is finalized even if "event: done" wasn't received
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && m.status === 'streaming' ? finalizeMessage(m) : m
          )
        )
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  parts: [
                    {
                      type: 'text',
                      text: 'Sorry, something went wrong. Please try again.',
                    },
                  ],
                  status: 'error',
                }
              : m
          )
        )
      } finally {
        setIsLoading(false)
      }
    },
    [coworkerId, input, isLoading]
  )

  const clearMessages = useCallback(() => {
    abortRef.current?.abort()
    setMessages([])
    setInput('')
    setIsLoading(false)
  }, [])

  return { messages, isLoading, input, setInput, sendMessage, clearMessages }
}
